import SiteFooter from '../../components/layout/SiteFooter';
import { Link, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import {
    ArrowRight, Calendar, ChevronDown, ChevronUp,
} from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';
import useRevealCards from '../../hooks/useRevealCards';
import notionsIllustration from '../../assets/notions-illustration.webp';
import Illustration from '../../components/ui/Illustration';
import { ILLU_COLOR } from '../../components/ui/ScopeIllustration';
import illuRevision from '../../assets/illustrations/fonctionnalites/revision-espacee.svg?raw';
import illuChoisir from '../../assets/illustrations/choisir-un-sujet.svg?raw';
import illuQuiz from '../../assets/illustrations/quiz-libre.svg?raw';
import illuExpress from '../../assets/illustrations/express.svg?raw';
import illuDocument from '../../assets/illustrations/fonctionnalites/document.svg?raw';
import illuFpe from '../../assets/illustrations/fonctionnalites/fonction-publique-etat.svg?raw';
import illuValidation from '../../assets/illustrations/fonctionnalites/validation-recherche.svg?raw';
import illuProgression from '../../assets/illustrations/fonctionnalites/suivi-progression.svg?raw';
import illuRedaction from '../../assets/illustrations/fonctionnalites/creation-manuelle.svg?raw';
import illuNotice from '../../assets/illustrations/fonctionnalites/explications-detaillees.svg?raw';
import illuOral from '../../assets/illustrations/fonctionnalites/relecture-humaine.svg?raw';


/**
 * /aide-a-la-preparation-concours-dgfip
 *
 * Landing publique SEO + acquisition pour les concours DGFiP
 * (Agent Cat C + Contrôleur Cat B + Inspecteur Cat A). Long format pédagogique
 * avec annales 2026, programme officiel, méthodologie et CTA gratuit.
 *
 * Ton : tutoiement, chaleureux, encourageant.
 * CTA principal : "Créer mon compte gratuit".
 */

// Agent : chiffres et épreuves lus sur la fiche officielle, les guides DGFiP 2026 et le rapport du jury 2025
// (docs/dgfip/inventaire-agent-2026/INVENTAIRE-AGENT-2026.md, §1).
const CONCOURS = [
    {
        slug: 'agent',
        nom: 'Agent des Finances Publiques',
        categorie: 'Catégorie C',
        niveau: 'Brevet',
        bgImage: '/img/dgfip-controleur.webp',
        stats: [
            { value: '13 830', label: 'inscrits en 2025 (externe)' },
            { value: '50 QCM', label: 'sans calculatrice (sujet zéro)' },
            { value: '3 épreuves', label: 'QCM, écrit, oral' },
            { value: '20 min', label: 'oral (coef 3)' },
        ],
        details: [
            'QCM de pré-admissibilité (1h30, coef 1)',
            'Cas pratiques sur dossier (3h, coef 2)',
            'Concours commun avec la Douane (branche administrative)',
        ],
    },
    {
        slug: 'ctrl',
        nom: 'Contrôleur des Finances Publiques',
        categorie: 'Catégorie B',
        niveau: 'Bac',
        bgImage: '/img/dgfip-controleur.webp',
        stats: [
            { value: '~1 500', label: 'postes / an' },
            { value: '≈ 2 150 €', label: 'net débutant (primes incl.)' },
            { value: '2 épreuves', label: 'à l\'écrit' },
            { value: '25 min', label: 'oral (coef 6)' },
        ],
        details: [
            'Analyse de dossier (3h, coef 4)',
            'Épreuve combinée : QCM + QRC à option (2h, coef 2)',
            'Affectations : impôts, trésoreries, recouvrement',
        ],
    },
    {
        slug: 'insp',
        nom: 'Inspecteur des Finances Publiques',
        categorie: 'Catégorie A',
        niveau: 'Licence (bac+3)',
        bgImage: '/img/dgfip-inspecteur.webp',
        stats: [
            { value: '~1 000', label: 'postes / an' },
            { value: '≈ 2 650 €', label: 'net débutant (primes incl.)' },
            { value: '2 épreuves', label: 'à l\'écrit' },
            { value: '25 min', label: 'oral (coef 6)' },
        ],
        details: [
            'Note de synthèse (4h, coef 5)',
            'Épreuve combinée : QCM + QRC à option (2h30, coef 3)',
            'Encadrement, contrôle fiscal, direction',
        ],
    },
];

const PROGRAMME_CONTROLEUR = [
    { titre: 'Connaissances générales', detail: 'Histoire de France, géographie, institutions françaises, Union européenne, culture numérique' },
    { titre: 'Français', detail: 'Grammaire, orthographe, vocabulaire' },
    { titre: 'Mathématiques', detail: 'Arithmétique, algèbre, proportionnalité, pourcentages' },
    { titre: 'Raisonnement logique', detail: 'Analyse de données, déduction, séries, intrus' },
    { titre: 'Options QRC (1 au choix)', detail: 'Droit, Comptabilité, HGGP, Mathématiques, Sciences économiques et sociales' },
];

const PROGRAMME_INSPECTEUR = [
    { titre: 'Note de synthèse', detail: 'Épreuve de méthode sur dossier documentaire (≤ 25 pages)' },
    { titre: 'QCM commun', detail: '40 à 45 questions sur 5 thèmes : environnement administratif/éco/financier, UE, culture numérique, raisonnement logique, anglais (niveau B1)' },
    { titre: 'Options QRC (1 parmi 9)', detail: 'Comptabilité, Droit privé, Droit public, Finances publiques, Sciences politiques, Mathématiques, Littérature, Histoire-Géographie, Analyse économique' },
];

const PROGRAMME_AGENT = [
    { titre: 'QCM de pré-admissibilité (1h30)', detail: 'Niveau brevet, calculatrice interdite, une réponse fausse est pénalisée. 8 rubriques : histoire, géographie, enseignement moral et civique, numérique et outils informatiques, actualité, organisation et missions des ministères économiques et financiers, mathématiques, raisonnement logique' },
    { titre: 'Écrit d\'admissibilité (3h)', detail: 'Cas pratiques sur un dossier documentaire : questions courtes, un développement structuré, puis un support de communication d\'une page' },
    { titre: 'Oral d\'admission (20 min)', detail: 'Présentation du parcours, échange avec le jury et mises en situation' },
];

const ETAPES_PREPARATION = [
    { mois: '6 mois avant', focus: 'Maîtriser le programme du tronc commun. Lire en parallèle l\'actualité institutionnelle et budgétaire.' },
    { mois: '3 mois avant', focus: 'Choisir et approfondir son option QRC. S\'entraîner sur des questions courtes.' },
    { mois: '1 mois avant', focus: 'Entraînement chronométré sur des séries de QCM. Identifier ses faiblesses et y revenir.' },
    { mois: '1 semaine avant', focus: 'Révisions ciblées, gestion du sommeil, repérage du centre d\'examen.' },
];

const FAQ = [
    {
        question: 'Quel est le programme du concours d\'agent des finances publiques ?',
        reponse: 'Depuis 2026, le concours externe d\'agent (catégorie C, niveau brevet) commence par un QCM de 1h30 sans calculatrice, en 8 rubriques : histoire, géographie, enseignement moral et civique, numérique, actualité, organisation et missions des ministères économiques et financiers, mathématiques et raisonnement logique. Viennent ensuite un écrit de cas pratiques sur dossier (3h) et un oral de 20 minutes. Une note inférieure à 5/20 est éliminatoire.',
    },
    {
        question: 'Le français est-il encore au QCM du concours d\'agent DGFiP ?',
        reponse: 'Non. Jusqu\'en 2025, le QCM portait sur l\'orthographe, le vocabulaire, la grammaire, les connaissances générales et le calcul. Depuis la rénovation de 2026, il n\'y a plus de français au QCM : il porte sur les 8 rubriques du programme. La qualité de la langue compte toujours à l\'écrit.',
    },
    {
        question: 'Comment MonQuiz aide-t-il à préparer le concours d\'agent ?',
        reponse: 'La thématique Fonction publique d\'État propose un parcours Agent : un bilan pour repérer tes points faibles, puis des sujets de niveau brevet sur l\'histoire-géographie, la citoyenneté, le numérique, les ministères économiques et financiers, les mathématiques, la lecture de graphiques et la logique. De nouveaux sujets arrivent régulièrement, signalés par un badge « Nouveau ».',
    },
    {
        question: 'Combien de candidats au concours Contrôleur DGFiP par an ?',
        reponse: 'Environ 50 000 candidats inscrits pour ~1 500 postes au concours externe Contrôleur. Le taux de réussite oscille autour de 3 à 5 %. C\'est sélectif mais largement préparable avec une méthode régulière.',
    },
    {
        question: 'Comment se préparer au concours Inspecteur DGFiP ?',
        reponse: 'Le concours rénové 2026 met l\'accent sur la note de synthèse (coef 5, 4h) et l\'épreuve par option (coef 3, 2h30). Il faut donc 50% du temps de prép sur la méthode note de synthèse + 50% sur l\'option choisie. Compte 6 à 9 mois de préparation sérieuse.',
    },
    {
        question: 'Quelle est la durée du concours QCM Contrôleur ?',
        reponse: 'L\'épreuve QCM/QRC dure 2 heures (coefficient 2). Elle comporte 30 QCM de tronc commun (sur 4 thèmes) puis 3 QRC sur l\'option choisie. Une mauvaise réponse au QCM est pénalisée — il faut savoir s\'abstenir quand on n\'est pas sûr.',
    },
    {
        question: 'Combien gagne un Contrôleur DGFiP en début de carrière ?',
        reponse: 'Avec les primes spécifiques DGFiP (régime indemnitaire de la filière fiscale + indemnités de stage), un Contrôleur débutant touche environ 2 100 à 2 200 € net par mois (la DGFiP paie mieux que le reste de la fonction publique d\'État). Le salaire évolue à partir de la titularisation, avec des progressions d\'échelon tous les 2 à 4 ans.',
    },
    {
        question: 'Quelles sont les annales DGFiP 2026 ?',
        reponse: 'Contrôleur 2026 : épreuve d\'analyse de dossier portait sur la sécurité routière ; les QCM/QRC options couvraient Droit, Compta, HGGP, Maths, SES. Inspecteur 2026 : note de synthèse sur le patrimoine immobilier de l\'État (cathédrale Notre-Dame, Villers-Cotterêts, politique immobilière de l\'État). Tu peux t\'entraîner sur des QCM inspirés de ces annales dans MonQuiz.',
    },
    {
        question: 'Quel est l\'âge limite pour passer le concours DGFiP ?',
        reponse: 'Il n\'y a pas d\'âge limite pour le concours externe. Le concours est ouvert à toute personne titulaire du diplôme requis (brevet pour Agent, bac pour Contrôleur, licence pour Inspecteur). De nombreux candidats en reconversion s\'y présentent.',
    },
    {
        question: 'Quels débouchés après le concours Inspecteur DGFiP ?',
        reponse: 'Un Inspecteur peut occuper des fonctions de contrôle fiscal, de gestion publique, d\'encadrement de services, ou évoluer vers les corps supérieurs (Inspecteur principal, Inspecteur divisionnaire). Mobilité possible vers d\'autres ministères ou postes en collectivité.',
    },
    {
        question: 'Faut-il un master pour passer le concours Inspecteur ?',
        reponse: 'Non. Une licence (bac+3) suffit. Beaucoup de candidats viennent de licence de droit, AES, économie, gestion, sciences politiques ou histoire. Le concours externe est ouvert à tous les diplômés, sans spécialité imposée.',
    },
];

export default function AidePreparationDGFiP() {
    usePageTitle('Aide à la préparation des concours DGFiP — Agent, Contrôleur & Inspecteur');
    useRevealCards(); // cartes .scope-card-landing révélées au défilement (sinon invisibles)
    const [searchParams] = useSearchParams();
    const fromParam = searchParams.get('from') || 'landing-dgfip';
    const registerHref = `/inscription?from=${encodeURIComponent(fromParam)}`;
    const scopeHref = '/fonction-publique-etat';

    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div className="livre-page aide-preparation-page aide-preparation-page--dgfip">
            <div className="livre-inner">

                {/* HERO */}
                <section className="livre-section livre-section--hero">
                    <div className="aide-freshness-badge">
                        <Calendar size={14} />
                        <span>Mis à jour pour la session {new Date().getFullYear() + 1} — Annales 2026 intégrées</span>
                    </div>
                    <h1 className="livre-title">
                        Préparer les concours <span className="livre-title-accent">DGFiP 2026-2027</span>
                    </h1>
                    <div className="livre-hero-text">
                        <p>
                            Tu veux devenir <strong>Agent</strong>, <strong>Contrôleur</strong> ou <strong>Inspecteur des Finances Publiques</strong> ?
                            MonQuiz propose une aide à la préparation : <strong>plus de 2 700 QCM dédiés DGFiP et Douanes</strong>, dont des sujets inspirés
                            des annales 2026, plus de <strong>5 600 QCM</strong> au total dans le scope Fonction Publique d'État
                            (sujets transverses, culture générale, droit, économie, comptabilité, méthodologie ENFiP),
                            et la grande majorité des notions des programmes (quelques points d'option restent peu traités).
                        </p>
                        <p style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
                            Ici on parle vrai : pas de promesse de réussite garantie, juste un outil qui te fait gagner du temps
                            sur l'entraînement et le suivi de tes faiblesses.
                        </p>
                    </div>
                    <div className="aide-hero-actions">
                        <Link to={registerHref} className="btn btn-primary decouvrir-cta-btn aide-hero-cta">
                            Créer mon compte gratuit <ArrowRight size={16} />
                        </Link>
                        <Link
                            to={scopeHref}
                            className="scope-card-landing aide-hero-scope-card"
                            style={{
                                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.75) 100%), url('/img/dgfip-controleur.webp')`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                color: '#fff',
                            }}
                        >
                            <div className="scope-card-icon scope-card-icon--illu">
                                <Illustration svg={illuFpe} color={ILLU_COLOR} className="aide-illu" />
                            </div>
                            <h3 style={{ color: '#fff' }}>Fonction Publique d'État</h3>
                            <p style={{ color: 'rgba(255,255,255,0.85)' }}>
                                5 600+ QCM préparation DGFiP & Douanes
                            </p>
                            <span className="scope-card-link" style={{ color: 'rgba(255,255,255,0.95)' }}>
                                Découvrir les quiz <ArrowRight size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: 'middle' }} />
                            </span>
                        </Link>
                    </div>
                </section>

                {/* LIENS VERS LES PAGES CALENDRIER (SEO — maillage descendant) */}
                <section className="livre-section">
                    <div className="aide-calendriers-grid">
                        {[
                            { href: '/dates-concours-agent-dgfip-2027', label: 'Agent DGFiP 2027' },
                            { href: '/dates-concours-controleur-dgfip-2027', label: 'Contrôleur DGFiP 2027' },
                            { href: '/dates-concours-inspecteur-dgfip-2027', label: 'Inspecteur DGFiP 2027' },
                        ].map((c) => (
                            <a key={c.href} href={c.href} style={{
                                display: 'flex', alignItems: 'center', gap: '0.75rem',
                                padding: '0.9rem 1.1rem', borderRadius: '14px',
                                border: '1px solid var(--card-border)', background: 'var(--card-bg)',
                                textDecoration: 'none', color: 'var(--text)',
                            }}>
                                <span style={{ fontSize: '1.4rem', lineHeight: 1 }} aria-hidden="true">📅</span>
                                <span style={{ flex: 1 }}>
                                    <strong>Calendrier {c.label}</strong>
                                    <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                        Dates clés + alerte à l'ouverture
                                    </span>
                                </span>
                                <span style={{ color: 'var(--primary)', fontWeight: 700 }} aria-hidden="true">→</span>
                            </a>
                        ))}
                    </div>
                </section>

                {/* FICHES CONCOURS (pages PHP /concours-*, maillage descendant) */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Les fiches des concours DGFiP</h2>
                    <div className="related-concours">
                        {[
                            { href: '/concours-dgfip', label: 'Les concours de la DGFiP : vue d’ensemble' },
                            { href: '/concours-controleur-des-finances-publiques', label: 'Concours de contrôleur des finances publiques' },
                            { href: '/concours-controleur-des-finances-publiques-qrc', label: 'QRC du contrôleur : format et exemple corrigé' },
                            { href: '/concours-controleur-des-finances-publiques-interne-special', label: 'Contrôleur : concours interne spécial' },
                            { href: '/concours-inspecteur-des-finances-publiques', label: 'Concours d’inspecteur des finances publiques' },
                        ].map(c => <a key={c.href} href={c.href}>{c.label}</a>)}
                    </div>
                </section>

                {/* COMPARATIF CONTRÔLEUR / INSPECTEUR */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Concours Agent (Cat C), Contrôleur (Cat B) et Inspecteur (Cat A) des Finances Publiques</h2>
                    <p className="livre-section-sub">
                        La DGFiP recrute par 3 concours externes : Agent (catégorie C, niveau brevet), Contrôleur (catégorie B)
                        et Inspecteur (catégorie A). Le programme et les épreuves diffèrent — voici l'essentiel pour choisir.
                    </p>
                    <div className="aide-concours-grid">
                        {CONCOURS.map((c) => {
                            return (
                                <article
                                    key={c.slug}
                                    className={`aide-concours-card aide-concours-card--${c.slug}`}
                                    style={{ backgroundImage: `url('${c.bgImage}')` }}
                                >
                                    <div className="aide-concours-card-content">
                                        <span className="aide-concours-card-cat">
                                            {c.categorie} — Niveau {c.niveau}
                                        </span>
                                        <h3>{c.nom}</h3>
                                        <div className="aide-concours-stats">
                                            {c.stats.map((s, j) => (
                                                <div key={j} className="aide-concours-stat">
                                                    <strong>{s.value}</strong>
                                                    <span>{s.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <ul className="aide-concours-details">
                                            {c.details.map((d, j) => <li key={j}>{d}</li>)}
                                        </ul>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </section>

                {/* PROGRAMME OFFICIEL */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Programme officiel des concours Agent, Contrôleur et Inspecteur DGFiP</h2>
                    <p className="livre-section-sub">
                        Les programmes sont fixés par arrêté ministériel et publiés au Journal Officiel.
                        MonQuiz propose des sujets pour chaque grande partie des QCM et des options, avec quelques points d'option encore peu traités. Les épreuves rédigées (note de synthèse, analyse de dossier) ne se préparent pas par QCM.
                    </p>

                    <div className="livre-section-block">
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Illustration svg={illuDocument} color={ILLU_COLOR} className="aide-heading-illu" /> Concours Agent (Cat C) — rénové en 2026
                        </h3>
                        <ul style={{ paddingLeft: '1.5rem' }}>
                            {PROGRAMME_AGENT.map((p, i) => (
                                <li key={i} style={{ marginBottom: '0.5rem' }}>
                                    <strong>{p.titre}</strong> — {p.detail}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="livre-section-block" style={{ marginTop: '1.5rem' }}>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Illustration svg={illuDocument} color={ILLU_COLOR} className="aide-heading-illu" /> Concours Contrôleur (Cat B)
                        </h3>
                        <ul style={{ paddingLeft: '1.5rem' }}>
                            {PROGRAMME_CONTROLEUR.map((p, i) => (
                                <li key={i} style={{ marginBottom: '0.5rem' }}>
                                    <strong>{p.titre}</strong> — {p.detail}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="livre-section-block" style={{ marginTop: '1.5rem' }}>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Illustration svg={illuDocument} color={ILLU_COLOR} className="aide-heading-illu" /> Concours Inspecteur (Cat A)
                        </h3>
                        <ul style={{ paddingLeft: '1.5rem' }}>
                            {PROGRAMME_INSPECTEUR.map((p, i) => (
                                <li key={i} style={{ marginBottom: '0.5rem' }}>
                                    <strong>{p.titre}</strong> — {p.detail}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* STRATÉGIE DE PRÉPARATION */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Stratégie de préparation au concours DGFiP en 4 étapes</h2>
                    <p className="livre-section-sub">
                        Plus tu commences tôt, plus tu peux étaler ta préparation et tester plusieurs options.
                        Voici un calendrier indicatif basé sur les retours des candidats reçus.
                    </p>
                    <div className="decouvrir-modes">
                        {ETAPES_PREPARATION.map((e, i) => (
                            <div key={i} className="decouvrir-mode">
                                <div className="decouvrir-mode-icon">
                                    {i === 0 && <Illustration svg={illuRevision} color={ILLU_COLOR} className="aide-illu" />}
                                    {i === 1 && <Illustration svg={illuChoisir} color={ILLU_COLOR} className="aide-illu" />}
                                    {i === 2 && <Illustration svg={illuQuiz} color={ILLU_COLOR} className="aide-illu" />}
                                    {i === 3 && <Illustration svg={illuExpress} color={ILLU_COLOR} className="aide-illu" />}
                                </div>
                                <div className="decouvrir-mode-body">
                                    <strong>{e.mois}</strong>
                                    <p>{e.focus}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ANNALES 2026 COMMENTÉES */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Annales du concours DGFiP 2026 (Agent, Contrôleur et Inspecteur)</h2>
                    <p className="livre-section-sub">
                        Les annales officielles restent la référence. Voici les sujets de la session 2026
                        que MonQuiz couvre avec des QCM inspirés (mono-réponse à 4 propositions, format MonQuiz).
                    </p>

                    <div className="decouvrir-honest decouvrir-honest--3">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuValidation} color={ILLU_COLOR} className="aide-heading-illu" /> Agent 2026 — sujet zéro du QCM rénové</h3>
                            <ul>
                                <li><strong>50 questions</strong> en 8 rubriques, 6 à 7 par rubrique</li>
                                <li><strong>Exemples</strong> — le Nil, la laïcité et la loi de 1905, la 5G, les cookies, la COP 30, le rôle de la Douane, la facturation électronique, une lecture de graphique, des énigmes de logique</li>
                                <li><strong>Niveau brevet</strong>, sans calculatrice</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuValidation} color={ILLU_COLOR} className="aide-heading-illu" /> Contrôleur 2026</h3>
                            <ul>
                                <li><strong>Analyse de dossier</strong> — Thème : sécurité routière (permis à points, ASSR, alcoolémie, prévention jeunes)</li>
                                <li><strong>QCM tronc commun</strong> — 30 questions (CG, Français, Maths, Logique)</li>
                                <li><strong>QRC options 2026</strong> — Droit (personnalité morale, responsabilité, hiérarchie des normes), Compta, HGGP, Maths, SES</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuValidation} color={ILLU_COLOR} className="aide-heading-illu" /> Inspecteur 2026</h3>
                            <ul>
                                <li><strong>Note de synthèse</strong> — Thème : patrimoine immobilier de l'État (Notre-Dame, Villers-Cotterêts, PIE, mécénat)</li>
                                <li><strong>QCM commun</strong> — 40 à 45 questions transverses (incluant l'anglais B1)</li>
                                <li><strong>QRC options 2026</strong> — Finances publiques, Compta, Droit privé/public, Sciences po, Maths, Littérature, Hist-Géo, Analyse éco</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* MÉTHODOLOGIE */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Méthodologie officielle ENFiP des épreuves DGFiP</h2>
                    <p className="livre-section-sub">
                        L'ENFiP (École Nationale des Finances Publiques) publie des guides de préparation détaillés.
                        MonQuiz transforme ces guides en QCM méthodologiques pour qu'ils restent dans ta tête.
                    </p>
                    <div className="decouvrir-modes">
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon"><Illustration svg={illuQuiz} color={ILLU_COLOR} className="aide-illu" /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode QCM Agent</strong>
                                <p>1h30 pour une cinquantaine de questions, 4 propositions, une réponse fausse pénalisée, l'absence de réponse vaut 0. Calculatrice interdite. Une note inférieure à 5/20 est éliminatoire.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon"><Illustration svg={illuQuiz} color={ILLU_COLOR} className="aide-illu" /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode QCM Contrôleur</strong>
                                <p>30 questions, 4 propositions, pénalité sur mauvaise réponse, case « Ann » pour s'abstenir. Gestion du temps recommandée : 30 min max pour le QCM + 1h30 pour la rédaction des QRC.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon"><Illustration svg={illuDocument} color={ILLU_COLOR} className="aide-illu" /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode Note de synthèse Inspecteur</strong>
                                <p>4h coef 5, structure intro/2-3 parties/conclusion, citation des documents, brouillon obligatoire, longueur 4-6 pages manuscrites.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon"><Illustration svg={illuDocument} color={ILLU_COLOR} className="aide-illu" /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Méthode Analyse de dossier Contrôleur</strong>
                                <p>3h coef 4, dossier ≤ 20 pages, 5 étapes : 15' lecture / 10' plan / 50' rédaction / 1h40 argumentation / 5' relecture.</p>
                            </div>
                        </div>
                        <div className="decouvrir-mode">
                            <div className="decouvrir-mode-icon"><Illustration svg={illuOral} color={ILLU_COLOR} className="aide-illu" /></div>
                            <div className="decouvrir-mode-body">
                                <strong>Préparation oral</strong>
                                <p>25 minutes coef 6, jury paritaire de 2 cadres DGFiP, présentation parcours 5 min max + échanges aptitudes/motivations + mises en situation.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CE QUE MONQUIZ APPORTE */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Comment MonQuiz t'aide à préparer le concours DGFiP</h2>
                    <div className="decouvrir-honest">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuValidation} color={ILLU_COLOR} className="aide-heading-illu" /> Ce que tu trouves chez nous</h3>
                            <ul>
                                <li><strong>Plus de 2 700 QCM dédiés DGFiP et Douanes</strong>, dont des sujets inspirés des annales 2026</li>
                                <li><strong>+ 2 900 QCM transverses</strong> (culture générale, droit, éco, compta, raisonnement) — utiles pour le tronc commun</li>
                                <li>La grande majorité des notions du QCM commun et des options (Contrôleur et Inspecteur)</li>
                                <li><strong>Un parcours Agent</strong> de niveau brevet : bilan, histoire-géographie, citoyenneté, numérique, ministères, mathématiques, graphiques</li>
                                <li>Explication rédigée avec source (article CGI, LPF, Constitution, etc.) pour chaque question</li>
                                <li>5 modes d'entraînement : quiz par sujet, quiz mélangé, smart quiz adaptatif, quiz sur erreurs, révisions du jour</li>
                                <li>Suivi de tes faiblesses par matière et par tag, automatique</li>
                                <li><strong>20 questions à réponse rédigée</strong> sur les options Droit, avec correction critère par critère</li>
                                <li>Plusieurs séries de QCM gratuites par scope pour tester avant de s'abonner</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuProgression} color={ILLU_COLOR} className="aide-heading-illu" /> Ce qui te fait progresser</h3>
                            <ul>
                                <li>Tu travailles uniquement les questions où tu te trompes</li>
                                <li>La répétition espacée garde tes connaissances vivantes</li>
                                <li>Tu identifies tes 5 faiblesses prioritaires en un coup d'œil</li>
                                <li>Les QCM méthodologiques te font intégrer les règles sans les relire</li>
                                <li>Tu changes de concours (Agent, Contrôleur, Inspecteur) en un clic si tu hésites</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="livre-section">
                    <h2 className="livre-section-title">S'entraîner à rédiger les QRC</h2>
                    <p className="livre-section-sub">
                        L'épreuve d'option ne se coche pas, elle s'écrit. Tu rédiges ta réponse,
                        puis tu vois, critère par critère, ce que la grille attendait : ce que tu as
                        traité, ce qui manque, ce qui est contredit.
                    </p>
                    <p className="livre-section-sub">
                        <a href="/concours-controleur-des-finances-publiques-qrc">Format des QRC du contrôleur, sujets 2026 des 5 options et exemple corrigé critère par critère →</a>
                    </p>
                    <div className="decouvrir-honest">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuRedaction} color={ILLU_COLOR} className="aide-heading-illu" /> Ce que ça te donne</h3>
                            <ul>
                                <li>Un entraînement à la rédaction, là où le QCM ne peut rien pour toi</li>
                                <li>La liste nommée de ce qui manque à ta réponse, pas seulement une note</li>
                                <li>Deux exemples corrigés visibles sans abonnement, pour juger sur pièce</li>
                                <li>Des questions inspirées des annales 2026, option Droit</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--bad">
                            <h3><Illustration svg={illuNotice} color={ILLU_COLOR} className="aide-heading-illu" /> Ce que ce n'est pas</h3>
                            <ul>
                                <li>Ni une note de concours, ni un avis de jury</li>
                                <li>Le style et la qualité de rédaction ne sont pas évalués</li>
                                <li>La correction mesure la couverture des critères de fond, rien d'autre</li>
                                <li>Un complément à ton entraînement, pas un substitut à la relecture d'un formateur</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* NOTIONS : fiches de révision + 20 questions (à la carte, deux guides gratuits) */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Des fiches de révision et 20 questions par notion</h2>
                    <div className="dgfip-notions">
                        <p className="livre-section-sub">
                            Une notion, c'est une fiche pour comprendre l'essentiel d'un sujet, puis 20 questions pour
                            vérifier ce que tu as retenu. La fiche se lit partie par partie, et sous chaque explication,
                            un lien te ramène à la partie qui donne la réponse.
                        </p>
                        <img className="dgfip-notions__art" src={notionsIllustration} alt="" width="240" height="138" loading="lazy" />
                    </div>
                    <div className="decouvrir-honest">
                        <div className="decouvrir-honest-col decouvrir-honest-col--good">
                            <h3><Illustration svg={illuDocument} color={ILLU_COLOR} className="aide-heading-illu" /> Ce que tu trouves</h3>
                            <ul>
                                <li><strong>Deux guides gratuits</strong> : « Le concours de contrôleur de A à Z » et « Le concours d'inspecteur de A à Z » (épreuves, coefficients, calendrier 2027, pièges classiques)</li>
                                <li>Des notions du programme : LOLF et budget de l'État, collectivités territoriales, hiérarchie des normes, institutions européennes, bilan, calcul des coûts…</li>
                                <li>Des fiches vérifiées sur les textes officiels, avec visuels (frises, schémas, exemples chiffrés)</li>
                                <li>Tes séances de test retrouvées dans tes résultats</li>
                            </ul>
                        </div>
                        <div className="decouvrir-honest-col decouvrir-honest-col--bad">
                            <h3><Illustration svg={illuNotice} color={ILLU_COLOR} className="aide-heading-illu" /> Ce qu'il faut savoir</h3>
                            <ul>
                                <li>En plus de l'abonnement : chaque notion s'achète à part (de 3 à 5 €), ou en pack de 5 ou 10</li>
                                <li>Une notion offerte avec l'abonnement 6 mois, deux avec 12 mois</li>
                                <li>Une fiche ne remplace pas le cours : elle cible un point précis</li>
                            </ul>
                        </div>
                    </div>
                    <div className="decouvrir-cta-actions" style={{ marginTop: 16 }}>
                        <Link to={registerHref} className="btn btn-primary decouvrir-cta-btn">
                            Créer mon compte gratuit <ArrowRight size={16} />
                        </Link>
                    </div>
                </section>

                {/* FAQ */}
                <section className="livre-section">
                    <h2 className="livre-section-title">Questions fréquentes sur le concours DGFiP</h2>
                    <div className="aide-faq">
                        {FAQ.map((item, i) => (
                            <div key={i} className="aide-faq-item">
                                <button
                                    type="button"
                                    className="aide-faq-question"
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    aria-expanded={openFaq === i}
                                >
                                    <span>{item.question}</span>
                                    {openFaq === i ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                </button>
                                {openFaq === i && (
                                    <div className="aide-faq-answer">{item.reponse}</div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* JSON-LD pour SEO et IA (FAQPage + Organization + Course + BreadcrumbList) */}
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify({
                                '@context': 'https://schema.org',
                                '@graph': [
                                    {
                                        '@type': 'FAQPage',
                                        mainEntity: FAQ.map(f => ({
                                            '@type': 'Question',
                                            name: f.question,
                                            acceptedAnswer: { '@type': 'Answer', text: f.reponse },
                                        })),
                                    },
                                    {
                                        '@type': 'Organization',
                                        name: 'MonQuiz.fr',
                                        url: 'https://monquiz.fr',
                                        logo: 'https://monquiz.fr/img/og-monquiz.webp',
                                        sameAs: ['https://monquiz.fr'],
                                        description: 'Plateforme française d\'entraînement aux concours par QCM avec plus de 5 600 QCM relus pour les concours DGFiP et Douanes.',
                                        contactPoint: {
                                            '@type': 'ContactPoint',
                                            email: 'contact@monquiz.fr',
                                            contactType: 'customer support',
                                            areaServed: 'FR',
                                            availableLanguage: 'French',
                                        },
                                    },
                                    {
                                        '@type': 'Course',
                                        name: 'Préparation au concours externe Agent des Finances Publiques (DGFiP)',
                                        description: 'Les 8 rubriques du QCM rénové en 2026 (niveau brevet, sans calculatrice) et des QCM de niveau brevet : une aide à la préparation du concours externe d\'agent administratif des finances publiques (catégorie C).',
                                        provider: {
                                            '@type': 'Organization',
                                            name: 'MonQuiz.fr',
                                            sameAs: 'https://monquiz.fr',
                                        },
                                        educationalLevel: 'Catégorie C (niveau Brevet)',
                                        teaches: 'Histoire, Géographie, Enseignement moral et civique, Numérique, Actualité, Ministères économiques et financiers, Mathématiques, Raisonnement logique',
                                        inLanguage: 'fr',
                                        hasCourseInstance: {
                                            '@type': 'CourseInstance',
                                            courseMode: 'online',
                                            url: 'https://monquiz.fr/fonction-publique-etat',
                                        },
                                    },
                                    {
                                        '@type': 'Course',
                                        name: 'Préparation au concours externe Contrôleur des Finances Publiques (DGFiP)',
                                        description: 'QCM inspirés des annales 2026, repères de méthode ENFiP et plus de 2 700 QCM dédiés : une aide à la préparation du concours externe de contrôleur des finances publiques (catégorie B).',
                                        provider: {
                                            '@type': 'Organization',
                                            name: 'MonQuiz.fr',
                                            sameAs: 'https://monquiz.fr',
                                        },
                                        educationalLevel: 'Catégorie B (niveau Bac)',
                                        teaches: 'Connaissances générales, Français, Mathématiques, Raisonnement logique, Droit, Comptabilité, HGGP, SES',
                                        inLanguage: 'fr',
                                        hasCourseInstance: {
                                            '@type': 'CourseInstance',
                                            courseMode: 'online',
                                            url: 'https://monquiz.fr/fonction-publique-etat',
                                        },
                                    },
                                    {
                                        '@type': 'Course',
                                        name: 'Préparation au concours externe Inspecteur des Finances Publiques (DGFiP)',
                                        description: 'QCM inspirés des annales 2026, repères de méthode pour la note de synthèse et QCM dédiés : une aide à la préparation du concours externe d\'inspecteur des finances publiques (catégorie A).',
                                        provider: {
                                            '@type': 'Organization',
                                            name: 'MonQuiz.fr',
                                            sameAs: 'https://monquiz.fr',
                                        },
                                        educationalLevel: 'Catégorie A (niveau Licence)',
                                        teaches: 'Note de synthèse, QCM commun (environnement administratif, UE, culture numérique), options QRC (Comptabilité, Droit, Finances publiques, Sciences politiques, Mathématiques, Littérature, Histoire-Géographie, Analyse économique)',
                                        inLanguage: 'fr',
                                        hasCourseInstance: {
                                            '@type': 'CourseInstance',
                                            courseMode: 'online',
                                            url: 'https://monquiz.fr/fonction-publique-etat',
                                        },
                                    },
                                    {
                                        '@type': 'BreadcrumbList',
                                        itemListElement: [
                                            { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://monquiz.fr' },
                                            { '@type': 'ListItem', position: 2, name: 'Fonction Publique d\'État', item: 'https://monquiz.fr/fonction-publique-etat' },
                                            { '@type': 'ListItem', position: 3, name: 'Préparation concours DGFiP', item: 'https://monquiz.fr/aide-a-la-preparation-concours-dgfip' },
                                        ],
                                    },
                                ],
                            }),
                        }}
                    />
                </section>

                {/* CTA FINAL */}
                <section className="livre-section livre-section--cta">
                    <h2 className="livre-section-title">Lance ta préparation au concours DGFiP</h2>
                    <p className="livre-section-sub">
                        L'inscription est gratuite. Tu accèdes immédiatement à plusieurs séries de QCM gratuites sur le scope DGFiP,
                        sans carte bancaire. Tu décides ensuite si tu passes en accès complet pour t'entraîner sur les
                        <strong> 5 600 QCM</strong> de la thématique DGFiP et Douanes (dont plus de 2 700 qui lui sont propres).
                    </p>
                    <div className="decouvrir-cta-actions">
                        <Link to={registerHref} className="btn btn-primary decouvrir-cta-btn">
                            Créer mon compte gratuit <ArrowRight size={16} />
                        </Link>
                        <Link to="/connexion" className="btn btn-secondary">
                            J'ai déjà un compte
                        </Link>
                    </div>
                </section>

            </div>

            <SiteFooter />
        </div>
    );
}
